import { createDepositInvoiceThunk } from '@spike/invoices-action';
import { confirmDepositThunk } from '@spike/payments-action';
import { fetchAddonServicesThunk } from 'actions/addonServices/AddonServicesActions';
import { fetchAgreementsThunk } from 'actions/agreements/AgreementsActions';
import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { setAuthToken } from 'actions/auth/AuthActions';
import {
    fetchStaffAvailabilityThunk,
    getNextAvailableDateThunk
} from 'actions/availability/AvailabilityActions';
import { createBookingForDepositThunk, createBookingThunk } from 'actions/bookings/BookingsActions';
import { BookingError, BookingErrorType } from 'actions/bookings/BookingsErrors';
import {
    lookupCustomerThunk,
    saveCustomerPetsThunk,
    saveCustomerThunk
} from 'actions/customer/CustomerActions';
import { CustomerError } from 'actions/customer/CustomerErrors';
import { fetchMarketplaceBySlugThunk } from 'actions/marketplace/MarketplaceActions';
import { MarketplaceError } from 'actions/marketplace/MarketplaceError';
import { fetchMasterDataThunk } from 'actions/masterData/MasterDataActions';
import {
    ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
    ONLINE_BOOKING_ADD_PET,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_SET_AVAILABILITY,
    ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE,
    ONLINE_BOOKING_SET_BOOKING_ITEMS,
    ONLINE_BOOKING_SET_CUSTOMER,
    ONLINE_BOOKING_SET_DATE,
    ONLINE_BOOKING_SET_LOADING,
    ONLINE_BOOKING_SET_SELECTED_STAFF_ID,
    ONLINE_BOOKING_SET_STAFFS,
    ONLINE_BOOKING_SET_STAFFS_BUSY,
    ONLINE_BOOKING_SET_STAFFS_FETCHED,
    ONLINE_BOOKING_SET_STAFFS_LOADING,
    ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE,
    ONLINE_BOOKING_SET_STEP,
    ONLINE_BOOKING_SET_TIME,
    ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON,
    ONLINE_BOOKING_UPDATE_PET_START,
    ONLINE_BOOKING_UPDATE_PET_SUCCESS,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { savePetThunk } from 'actions/pets/PetsActions';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchStaffsThunk } from 'actions/staffs/StaffsActions';
import { createWrapper } from 'api/ApiClient';
import { push } from 'connected-react-router';
import moment, { Moment } from 'moment-timezone';
import {
    getOnlineBookingAvailableStaffs,
    getOnlineBookingEarliestNextAvailableDate,
    getOnlineBookingTotalDuration
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingState } from 'reducers/onlineBooking/OnlineBookingState';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AddonService } from 'types/AddonService';
import { AgreementSignature } from 'types/Agreement';
import { AlertScreen } from 'types/Alert';
import { SaveAppointment } from 'types/Appointment';
import { AvailabilityHour } from 'types/Availability';
import { BookingChannel, BookingStatus, SaveBooking } from 'types/Booking';
import { Customer } from 'types/Customer';
import { MarketplaceScheduleType } from 'types/Marketplace';
import { OnlineBookingItem, OnlineBookingStep } from 'types/OnlineBooking';
import { Order, OrderizableType } from 'types/Order';
import { Pet } from 'types/Pet';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';
import { petsMatch } from 'utils/petUtils';
import { getServiceDurationByPet } from 'utils/serviceUtils';
import { v4 as uuid } from 'uuid';

const ONLINE_BOOKING_CONFIG = {
    AVAILABILITY_WEEKS: 8
};

const errorHandler = (error: unknown): OnlineBookingAction => {
    console.error(error);

    return {
        type: ONLINE_BOOKING_ERROR
    };
};

const setLoading = (loading: boolean): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_LOADING,
        payload: {
            loading
        }
    };
};

export const setOnlineBookingStepThunk = (
    step: OnlineBookingStep
): ThunkAction<void, RootState, null, RootAction> => {
    return dispatch => {
        dispatch(setOnlineBookingStep(step));
    };
};

export const onlineBookingBackThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return (dispatch, getState) => {
        const state = getState();
        const { onlineBookingPreferences } = state.marketplace.data;
        const { isReturningClient, step } = state.onlineBooking;

        switch (step) {
            case OnlineBookingStep.Signin:
                dispatch(setOnlineBookingStep(OnlineBookingStep.Home));
                break;
            case OnlineBookingStep.NewClientForm:
                dispatch(setOnlineBookingStep(OnlineBookingStep.Home));
                break;
            case OnlineBookingStep.AddPet:
                dispatch(
                    setOnlineBookingStep(
                        isReturningClient
                            ? OnlineBookingStep.SelectPets
                            : OnlineBookingStep.NewClientForm
                    )
                );
                break;
            case OnlineBookingStep.SelectPets: {
                if (isReturningClient) return;

                dispatch(setOnlineBookingStep(OnlineBookingStep.NewClientForm));
                break;
            }
            case OnlineBookingStep.SelectServices:
                dispatch(setOnlineBookingStep(OnlineBookingStep.SelectPets));
                break;
            case OnlineBookingStep.SelectStaff:
                dispatch(setOnlineBookingStep(OnlineBookingStep.SelectServices));
                break;
            case OnlineBookingStep.SelectDateTime:
                if (!onlineBookingPreferences.allowSelectStaff) {
                    dispatch(setOnlineBookingStep(OnlineBookingStep.SelectServices));
                    break;
                }

                dispatch(setSelectedStaffId(null));
                dispatch(setOnlineBookingDate(undefined));
                dispatch(setOnlineBookingTime(undefined));
                dispatch(setOnlineBookingStep(OnlineBookingStep.SelectStaff));
                break;
            case OnlineBookingStep.NotesAndAgreements:
                dispatch(setOnlineBookingStep(OnlineBookingStep.SelectDateTime));
                break;
        }
    };
};

export const initOnlineBookingThunk = (
    marketplaceSlug: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(setLoading(true));

            const marketplace = await dispatch(fetchMarketplaceBySlugThunk(marketplaceSlug));

            await dispatch(fetchMasterDataThunk(marketplace.id));
            dispatch(fetchServicesThunk(marketplace.id));
            dispatch(fetchAddonServicesThunk(marketplace.id));
            const staffs = await dispatch(fetchStaffsThunk(marketplace.id));

            dispatch(setOnlineBookingStaffs(staffs));
            dispatch(setLoading(false));
        } catch (error) {
            dispatch(errorHandler(error));

            if (error instanceof MarketplaceError) {
                // Redirect to 404 page
                dispatch(push('/'));
            }
        }
    };
};

export const submitOnlineBookingHomeThunk = (
    step: OnlineBookingStep
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(setOnlineBookingStep(step));
    };
};

export const submitOnlineBookingNewCustomerThunk = (
    customer: Customer
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const statePets = getState().onlineBooking.pets;
        const hasPets = statePets.length > 0;

        dispatch(
            setCustomer(
                {
                    ...customer,
                    // Keep the existing pets in the state
                    pets: [...statePets]
                },
                false
            )
        );

        if (hasPets) {
            dispatch(setOnlineBookingStep(OnlineBookingStep.SelectPets));
        } else {
            dispatch(setOnlineBookingStep(OnlineBookingStep.AddPet));
        }
    };
};

export const submitOnlineBookingReturningCustomerThunk = (
    customer: Customer
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(setCustomer(customer, true));
        dispatch(setOnlineBookingStep(OnlineBookingStep.SelectPets));
    };
};

export const submitOnlineBookingAddPetThunk = (
    pet: Pet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(addPet(pet));
        dispatch(setOnlineBookingStep(OnlineBookingStep.SelectPets));
    };
};

export const submitOnlineBookingSelectPetsThunk = (
    pets: Array<Pet>
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(setBookingItems(pets.map(pet => ({ pet, service: undefined, addons: [] }))));
        dispatch(setOnlineBookingStep(OnlineBookingStep.SelectServices));
    };
};

export const submitOnlineBookingSelectServicesThunk = (): ThunkAction<
    void,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        const state = getState();
        const staffs = state.staffs.staffs;
        const { onlineBookingPreferences } = state.marketplace.data;

        if (!onlineBookingPreferences.allowSelectStaff) {
            dispatch(setOnlineBookingStep(OnlineBookingStep.SelectDateTime));
            return;
        }

        // Reset staff state
        dispatch(setOnlineBookingStaffs(staffs));
        dispatch(setSelectedStaffId(null));

        dispatch(setOnlineBookingStep(OnlineBookingStep.SelectStaff));
    };
};

export const submitOnlineBookingSelectStaffThunk = (
    staffId: number | null
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const state = getState();

        const onlineBooking = state.onlineBooking;
        const earliestNextAvailableDate = getOnlineBookingEarliestNextAvailableDate(state);

        const selectedStaffNextAvailableDate = onlineBooking.staffs.find(
            item => item.staff.id === staffId
        )?.nextAvailableAt;

        if (selectedStaffNextAvailableDate) {
            dispatch(setOnlineBookingDate(selectedStaffNextAvailableDate.date));
        }

        if (earliestNextAvailableDate && !selectedStaffNextAvailableDate) {
            dispatch(setOnlineBookingDate(earliestNextAvailableDate));
        }

        dispatch(setAvailability([]));
        dispatch(setSelectedStaffId(staffId));
        dispatch(setOnlineBookingStep(OnlineBookingStep.SelectDateTime));
    };
};

export const submitOnlineBookingSelectDateTimeThunk = (): ThunkAction<
    void,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const state = getState();

            const marketplaceId = state.marketplace.data.id;
            const bookingItems = state.onlineBooking.bookingItems;

            const petsToSave = bookingItems
                .filter(item => item.pet.id === undefined)
                .map(item => item.pet);

            let customer = {
                ...state.onlineBooking.customer,
                pets: [...petsToSave]
            };

            if (!customer) {
                throw new Error('Customer not found');
            }

            let savedCustomer: Customer | null = null;

            const lookupCustomer = await dispatch(
                lookupCustomerThunk(customer.email, marketplaceId)
            );

            if (lookupCustomer) {
                // Exclude the pets that already exist in the lookup customer
                customer = {
                    ...customer,
                    pets: customer.pets.filter(
                        pet => !lookupCustomer.pets.find(p => petsMatch(p, pet))
                    )
                };

                // Existing customer found, update data & pets
                savedCustomer = await dispatch(
                    saveCustomerThunk(
                        {
                            ...customer,
                            id: lookupCustomer.id
                        },
                        'online-booking'
                    )
                );
            }

            // Existing customer with new pets
            if (!lookupCustomer && customer.id && petsToSave.length > 0) {
                savedCustomer = await dispatch(saveCustomerPetsThunk(customer, petsToSave));
            }

            // Create a new customer & pets
            if (!lookupCustomer && !customer.id) {
                const randomPassword = Math.random().toString(36).substring(2, 15);

                savedCustomer = await dispatch(
                    saveCustomerThunk(customer, 'online-booking', randomPassword)
                );
            }

            // Update the booking items with the saved customer's pets
            dispatch(
                setBookingItems(
                    bookingItems.map(item => {
                        const petData = savedCustomer?.pets?.find(pet => petsMatch(pet, item.pet));

                        if (!petData) return item;

                        return {
                            ...item,
                            pet: petData
                        };
                    })
                )
            );

            if (!savedCustomer) {
                throw new Error('Customer not found');
            }

            dispatch(setAuthToken(String(savedCustomer.token)));
            dispatch(setCustomer(savedCustomer, true));

            // Fetch agreements
            await dispatch(fetchAgreementsThunk(Number(savedCustomer.id)));

            await dispatch(fetchMarketplaceBySlugThunk(state.marketplace.data.slug));

            dispatch(setOnlineBookingStep(OnlineBookingStep.NotesAndAgreements));
        } catch (error) {
            dispatch(errorHandler(error));

            const step = OnlineBookingStep.NewClientForm;
            let message = 'Oops, something went wrong.';
            const alertScreen = AlertScreen.OnlineBookingNewClientForm;

            if (error instanceof CustomerError) {
                message = error.message;
            }

            dispatch(setOnlineBookingStep(step));
            dispatch(addAlertThunk(message, alertScreen));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const submitOnlineBookingNotesAndAgreementsThunk = (
    notes: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const state = getState();
            const {
                id: marketplaceId,
                address,
                deposit,
                timeZone,
                scheduleType,
                onlineBookingPreferences
            } = state.marketplace.data;
            const { customer, date, time, selectedStaffId, bookingItems } = state.onlineBooking;

            const addressId = address?.id;
            const customerId = customer?.id;

            if (!customerId) {
                throw new Error('Customer not found');
            }

            if (!date) {
                throw new Error('Date not found');
            }

            if (!time) {
                throw new Error('Time not found');
            }

            if (!addressId) {
                throw new Error('Marketplace address not found');
            }

            const today = moment().tz(timeZone);
            const collectDeposit = deposit && !onlineBookingPreferences.requireBookingRequest;

            const bookingDate = date
                .clone()
                .set('hour', time.hour)
                .set('minute', time.minute)
                .tz(timeZone, true);

            let bookingStatus = BookingStatus.CONFIRMED;

            if (onlineBookingPreferences.requireBookingRequest) {
                bookingStatus = BookingStatus.REQUESTED;
            }

            if (collectDeposit) {
                bookingStatus = BookingStatus.PENDING;
            }

            const bookingStaffId = Number(selectedStaffId ?? time.staffId);

            const order: Order | undefined = bookingItems.some(item => item.addons.length > 0)
                ? {
                      uuid: uuid(),
                      marketplaceId,
                      customerId: customerId,
                      issuedAt: today,
                      items: bookingItems.flatMap(item =>
                          item.addons.map(addon => ({
                              uuid: uuid(),
                              quantity: 1,
                              discount: 0,
                              staffId: bookingStaffId,
                              amount: addon.price,
                              orderableId: addon.id,
                              orderableType: OrderizableType.AddonService,
                              petId: Number(item.pet.id),
                              description: addon.name,
                              marketplaceId,
                              startedAt: bookingDate,
                              orderizableId: addon.id,
                              orderizableType: OrderizableType.AddonService
                          }))
                      )
                  }
                : undefined;

            let appointmentDate = bookingDate.clone();

            const bookingData: SaveBooking = {
                uuid: uuid(),
                marketplaceId,

                staffId: bookingStaffId,
                customerId: customerId,

                order,

                appointments: bookingItems.reduce((acc, item) => {
                    if (!item.service) {
                        return acc;
                    }

                    acc.push({
                        uuid: uuid(),
                        petId: item.pet.id ?? 0,
                        serviceId: item.service?.id ?? 0,
                        addressId: addressId,
                        customerId: customerId,
                        staffId: bookingStaffId,
                        createdBy: bookingStaffId,
                        notes: notes ?? '',
                        channel: BookingChannel.ONLINE_BOOKING,
                        status: bookingStatus,
                        bookedAt: appointmentDate.clone()
                    });

                    // If the marketplace has a work schedule,
                    // we need to add the duration of the service to the next appointment date
                    if (scheduleType === MarketplaceScheduleType.Work) {
                        const duration = getServiceDurationByPet(
                            item.service!,
                            item.pet.type,
                            item.pet.size
                        );

                        if (!duration) {
                            return acc;
                        }

                        appointmentDate = appointmentDate.add(duration, 'minutes');
                    }

                    return acc;
                }, [] as Array<SaveAppointment>)
            };

            if (collectDeposit) {
                const booking = await dispatch(createBookingForDepositThunk(bookingData));
                dispatch(setOnlineBookingStep(OnlineBookingStep.Payment));

                await dispatch(createDepositInvoiceThunk(createWrapper(state), booking.id));
            } else {
                await dispatch(createBookingThunk(bookingData));
                dispatch(setOnlineBookingStep(OnlineBookingStep.Success));
            }
        } catch (error) {
            dispatch(errorHandler(error));

            let errorMessage = 'Oops, something went wrong.';
            let bookingStep = OnlineBookingStep.NotesAndAgreements;
            let alertScreen = AlertScreen.OnlineBookingNotesAndAgreements;

            if (error instanceof BookingError && error.type === BookingErrorType.AlreadyBooked) {
                bookingStep = OnlineBookingStep.SelectDateTime;
                alertScreen = AlertScreen.OnlineBookingSelectDateTime;
            }

            if (error instanceof BookingError) {
                errorMessage = error.message;
            }

            dispatch(addAlertThunk(errorMessage, alertScreen));
            dispatch(setOnlineBookingStep(bookingStep));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const submitOnlineBookingPaymentThunk = (
    invoiceId: number,
    sessionId: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const state = getState();

            await dispatch(
                confirmDepositThunk(createWrapper(state), invoiceId, 'credit_card', sessionId)
            );

            dispatch(setLoading(false));
            dispatch(setOnlineBookingStep(OnlineBookingStep.Success));
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const updateOnlineBookingPetThunk = (
    pet: Pet
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(updatePetStart());

        let savedPet: Pet | null = null;

        if (pet.id) {
            savedPet = await dispatch(savePetThunk(pet));
        }

        if (!pet.id) {
            savedPet = pet;
        }

        if (!savedPet) {
            // TODO: Handle error
            return;
        }

        dispatch(updatePetSuccess(savedPet));
    };
};

const updatePetStart = (): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_UPDATE_PET_START
    };
};

const updatePetSuccess = (pet: Pet): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_UPDATE_PET_SUCCESS,
        payload: {
            pet
        }
    };
};

export const fetchOnlineBookingStaffNextAvailableDateThunk = (
    date: Moment,
    staffIds: Array<number>
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            const onlineBooking = state.onlineBooking;

            const petsCount = onlineBooking.bookingItems.length;
            const availableStaffs = getOnlineBookingAvailableStaffs(state);
            const totalServicesDuration = getOnlineBookingTotalDuration(state);

            const availableStaffIds = availableStaffs.flatMap(item => item.staff.id);

            // Set loading state for staffs that are being checked
            dispatch(
                setStaffsLoading(
                    new Map(
                        availableStaffIds
                            .filter(staffId => staffIds.includes(staffId))
                            .map(staffId => [staffId, true])
                    )
                )
            );

            // Fetch availability for given staffs
            const availability = await dispatch(
                getNextAvailableDateThunk(
                    date,
                    petsCount,
                    totalServicesDuration,
                    ONLINE_BOOKING_CONFIG.AVAILABILITY_WEEKS,
                    staffIds
                )
            );

            const availableHours = availability.flatMap(item => item.availableHours);

            const staffIdsWithAvailability = availableHours.flatMap(hour => hour.staffAvailable);

            const nonAvailableStaffs = availableStaffIds
                // Check if staff is already fetched
                .filter(id => onlineBooking.staffs.find(item => item.staff.id === id)?.fetched)
                // Check if staff is not in the current staffIds
                .filter(id => staffIds.includes(id))
                // Check if staff is not available
                .filter(id => !staffIdsWithAvailability.includes(id))
                .map(id => ({
                    staffId: id,
                    weeksCount: ONLINE_BOOKING_CONFIG.AVAILABILITY_WEEKS
                }));

            // Set next available date for staffs
            dispatch(setStaffsNextAvailableDate(availableHours));
            // Set staffs as fetched
            dispatch(setStaffsFetched(staffIds));

            // Set staffs as busy
            if (nonAvailableStaffs.length > 0) {
                dispatch(setStaffsBusy(nonAvailableStaffs));
            }

            // Find next staffs to be checked
            const remainingStaffIds = availableStaffIds.filter(
                staffId => !staffIdsWithAvailability.includes(staffId) && staffIds.includes(staffId)
            );

            if (availableHours.length > 0 && remainingStaffIds.length > 0) {
                // Take the first available hour to check next (previous dates are already checked)
                const nextDate = availableHours[0].datetime;
                dispatch(
                    fetchOnlineBookingStaffNextAvailableDateThunk(nextDate, remainingStaffIds)
                );
            }

            // If all staffs are busy on first fetch, set them as busy
            if (remainingStaffIds.length === staffIds.length && availableHours.length === 0) {
                dispatch(
                    setStaffsBusy(
                        staffIds.map(id => ({
                            staffId: id,
                            weeksCount: ONLINE_BOOKING_CONFIG.AVAILABILITY_WEEKS
                        }))
                    )
                );
            }
        } catch (error) {
            dispatch(errorHandler(error));
        }
    };
};

export const goToNextAvailableDateThunk = (
    date: Moment
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const state = getState();

            const bookingItems = state.onlineBooking.bookingItems;
            const petsCount = state.onlineBooking.bookingItems.length;
            const totalServicesDuration = getOnlineBookingTotalDuration(state);

            const staffIds = state.onlineBooking.staffs
                .filter(item => {
                    return bookingItems.some(bookingItem => {
                        return bookingItem.service?.staffIds?.includes(item.staff.id);
                    });
                })
                .map(item => item.staff.id);

            const availability = await dispatch(
                getNextAvailableDateThunk(
                    date,
                    petsCount,
                    totalServicesDuration,
                    ONLINE_BOOKING_CONFIG.AVAILABILITY_WEEKS,
                    state.onlineBooking.selectedStaffId
                        ? [state.onlineBooking.selectedStaffId]
                        : staffIds
                )
            );

            const availableHours = availability.flatMap(item => item.availableHours);

            if (availableHours.length > 0) {
                const nextDate = availableHours[0].datetime;

                dispatch(fetchOnlineBookingStaffAvailabilityThunk(nextDate));
                dispatch(setOnlineBookingDate(nextDate));
            }
        } catch (error) {
            dispatch(errorHandler(error));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const fetchOnlineBookingStaffAvailabilityThunk = (
    date: Moment
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const { onlineBooking } = getState();

            const staffId = onlineBooking.selectedStaffId;
            const petsCount = onlineBooking.bookingItems.length;
            const totalDuration = getOnlineBookingTotalDuration(getState());

            // Only fetch availability for staffs that can perform selected services
            let staffIds = onlineBooking.staffs
                .filter(item => {
                    return onlineBooking.bookingItems.some(bookingItem => {
                        return bookingItem.service?.staffIds?.includes(item.staff.id);
                    });
                })
                .map(item => item.staff.id);

            if (staffId) {
                staffIds = [staffId];
            }

            const availability = await dispatch(
                fetchStaffAvailabilityThunk(date, staffIds, petsCount, totalDuration)
            );

            dispatch(setAvailability(availability[0].availableHours));
        } catch (error) {
            dispatch(errorHandler(error));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

const setAvailability = (availability: Array<AvailabilityHour>): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_AVAILABILITY,
        payload: { availability }
    };
};

const setStaffsLoading = (loadingState: Map<number, boolean> | boolean): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STAFFS_LOADING,
        payload: {
            loadingState
        }
    };
};

const setStaffsNextAvailableDate = (hours: Array<AvailabilityHour>): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE,
        payload: {
            hours
        }
    };
};

const setStaffsFetched = (staffIds: Array<number>): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STAFFS_FETCHED,
        payload: { staffIds }
    };
};

const setStaffsBusy = (
    staffs: Array<{ staffId: number; weeksCount: number }>
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STAFFS_BUSY,
        payload: {
            staffs
        }
    };
};

export const setOlbBookingItemService = (
    petUuid: string,
    service: Service
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE,
        payload: {
            petUuid,
            service
        }
    };
};

export const toggleOlbBookingItemAddon = (
    petUuid: string,
    addon: AddonService,
    isSelected: boolean
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON,
        payload: {
            isSelected,
            petUuid,
            addon
        }
    };
};

const setOnlineBookingStep = (step: OnlineBookingStep): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STEP,
        payload: {
            step
        }
    };
};

const setCustomer = (customer: Customer, isReturningClient: boolean): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_CUSTOMER,
        payload: {
            customer,
            isReturningClient
        }
    };
};

const addPet = (pet: Pet): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_ADD_PET,
        payload: {
            pet
        }
    };
};

const setBookingItems = (items: Array<OnlineBookingItem>): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_BOOKING_ITEMS,
        payload: { items }
    };
};

const setSelectedStaffId = (
    staffId: OnlineBookingState['selectedStaffId']
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_SELECTED_STAFF_ID,
        payload: {
            staffId
        }
    };
};

const setOnlineBookingStaffs = (staffs: Array<Staff>): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_STAFFS,
        payload: {
            staffs
        }
    };
};

export const setOnlineBookingDate = (date: OnlineBookingState['date']): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_DATE,
        payload: { date }
    };
};

export const setOnlineBookingTime = (time: OnlineBookingState['time']): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_SET_TIME,
        payload: { time }
    };
};

export const addOnlineBookingAgreementSignature = (
    signature: AgreementSignature
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
        payload: { signature }
    };
};
